import { TreatmentActivitiesDayByDayResponseDto } from "@/data/dto/patient-treatement-detail-list/treatment-activities-day-by-day-response-dto";
import { TreatmentActivitiesDayByDayInfoEntity } from "@/domain/entity/treatment/treatment-activities-day-by-day-info-entity";
import { formatLocalToHHmm, formatLocalToYyyyMmDd, serverDateToLocalDate } from "@/utils/date-util";
import { getActivityState } from "@/utils/enum/activity-state-enum";
import { LIGHT_ESTIMATION_MAX_COUNT } from "@/utils/global-constants";

export class TreatmentActivityDetailInfoMapper {
  /**
   * 활동 일자별 상세 정보를 엔티티 배열로 변환합니다.
   * @param endDate 활동 종료 날짜 (Date 객체)
   * @param dto progressDay별 활동 데이터 DTO
   * @param startDate 활동 시작 날짜 (string, ISO 형식 권장)
   * @returns TreatmentActivitiesDayByDayInfoEntity 배열
   */
  static toEntity(
    endDate: Date,
    dto: TreatmentActivitiesDayByDayResponseDto,
    startDate: string
  ): TreatmentActivitiesDayByDayInfoEntity[] {
    const mapResult: TreatmentActivitiesDayByDayInfoEntity[] = [];

    // maxDateOfDay를 startDate와 현재 날짜의 일수 차이로 계산
    const startDateValue = serverDateToLocalDate(startDate);
    // 시간 차이(ms) -> 일수로 변환, 1일차부터 시작하므로 +1
    const maxDateOfDay = Math.max(
      ...Object.keys(dto).map((key) => parseInt(key)),
      Math.floor((endDate.getTime() - (startDateValue?.getTime() ?? 0)) / (1000 * 60 * 60 * 24)) + 1
    );

    /**
     * DTO의 각 progressDay(진행 일차)별로 데이터를 변환하여 mapResult 배열에 추가합니다.
     * - 각 progressDay 별로 시작/종료시간, 상태, playTime 등 처리
     */
    Object.keys(dto).forEach((progressDayStr) => {
      // progressDayStr을 숫자로 변환하여 일차(progressDay)로 사용
      const progressDay = parseInt(progressDayStr);
      // 해당 일차의 데이터 추출
      const dayData = dto[progressDay];

      // 서버에서 전달받은 startTime을 로컬 Date 객체로 변환
      const startTime = serverDateToLocalDate(dayData.startTime);
      // 시작 시각을 HH:mm 형태의 문자열로 변환 (예: 09:00)
      const startTimeStr = formatLocalToHHmm(startTime);

      // 서버에서 전달받은 endTime을 로컬 Date 객체로 변환
      const endTime = serverDateToLocalDate(dayData.endTime);
      // 종료 시각을 HH:mm 형태의 문자열로 변환 (예: 09:30)
      const endTimeStr = formatLocalToHHmm(endTime);

      // 활동 상태값을 enum 값으로 변환
      const newState = getActivityState(dayData.state);

      // 활동 플레이 시간 포맷팅
      // 둘 다 값이 없으면 "-"로 표시, 하나라도 있으면 "시작~종료" 또는 "시작~" 혹은 "~종료" 형태로 표기
      let playTime = "";
      if (startTimeStr == "NaN:NaN" && endTimeStr == "NaN:NaN") {
        playTime = "-";
      } else if (startTimeStr == "NaN:NaN" && endTimeStr != "NaN:NaN") {
        playTime = `${startTimeStr}~${endTimeStr}`;
      } else if (startTimeStr != "NaN:NaN" && endTimeStr == "NaN:NaN") {
        playTime = `${startTimeStr}~`;
      } else {
        playTime = `${startTimeStr}~${endTimeStr}`;
      }

      // 변환한 데이터를 결과 배열에 추가
      mapResult.push({
        // API 응답에 uuid가 없는 활동은 상세보기(차트) 조회가 불가능하므로 null로 유지
        uuid: dayData.uuid || null,
        dateOfDay: progressDay, // N일차
        day: progressDay, // N일차
        date: dayData.dateOfDay, // 해당 일자 (yyyy-mm-dd)
        stateSummary: {
          state: newState, // 활동 상태
          dailyProgress: dayData.dailyProgress, // 일일 진행 퍼센트
        },
        lightEstimation: `${dayData.lightEstimationCount || 0}/${LIGHT_ESTIMATION_MAX_COUNT}`, // 빛 평가 현황 (예: 2/3)
        playTime: playTime, // 플레이 시간 포맷 결과
      });
    });

    /**
     * 시작일부터 최대 일자까지 "비어 있는 일차"도 누락 없이 모두 포함시켜 배열을 만들어줍니다.
     * mapResult에 없는 일차(i)는 빈 값(-, 상태없음 등)으로, 있으면 그대로 유지
     */
    const addEmptyDayResult: TreatmentActivitiesDayByDayInfoEntity[] = [];
    for (let i = 1; i <= maxDateOfDay; i++) {
      // 현재 일차(i)가 mapResult에 존재하는지 확인
      const obj = mapResult.find((item) => item.dateOfDay === i);

      if (!obj) {
        // 해당 일차 데이터가 없으면 빈 데이터로 만듦
        // startDate를 기준으로 i번째 날짜 계산
        const date = serverDateToLocalDate(startDate);
        if (date === null) {
          // startDate 변환 실패시 건너뜀 (일반적으로 발생하지 않음)
          continue;
        }
        date.setDate(date.getDate() + (i - 1)); // i일 뒤 날짜로 세팅
        const prevDate = formatLocalToYyyyMmDd(date); // yyyy-mm-dd 포맷 문자열 생성

        // 빈 데이터를 결과에 추가
        addEmptyDayResult.push({
          uuid: null,
          dateOfDay: i, // N일차
          day: i,
          date: prevDate, // 해당 일자
          stateSummary: {
            state: getActivityState(0), // 상태 없음(기본값)
            dailyProgress: 0, // 일일 진행 퍼센트
          },
          lightEstimation: "-", // 빛 평가 없음
          playTime: "-", // 플레이타임 없음
        });
      } else {
        // 기존 일차 데이터 그대로 추가

        addEmptyDayResult.push(obj);
      }
    }

    /**
     * 결과 배열을 "최신 일차부터 과거 일차"로 내림차순 정렬하여 반환
     * (즉, dateOfDay가 큰 것이 먼저 나옴)
     */
    return addEmptyDayResult.sort((a, b) => b.dateOfDay - a.dateOfDay);
  }
}
